import axios, {AxiosResponse} from 'axios';
import {Status} from '../models/enums';
import {routes} from '../routes/routes';
import axiosInstance from '../utils/AxiosInstans';
import {
  GroupClassRoomParticipantsQuery,
  GroupClassRoomPreReserveQuery,
  GroupClassRoomQuery,
} from './models/requestQueries';
import {toOptionalOrganizationUnitId} from '../utils/helpers/organizationUnits';
import {
  GroupClassRoomParticipantsResponse,
  GroupClassRoomResponse,
  GroupClassRoomServicesResponse,
  OrganizationUnitResponse,
} from './models/response/GroupClassRoomResService';
import {AuthResponseSignUpDto} from './models/response/ReservationResService';
import {handleMutationError} from '../utils/helpers/errorHandler';

const {baseUrl, groupClassRoom} = routes;

const GroupClassRoomService = {
  GetAll: async (
    query?: GroupClassRoomQuery,
    signal?: AbortSignal,
  ): Promise<GroupClassRoomResponse> => {
    try {
      const response = await axiosInstance.get<GroupClassRoomResponse>(
        baseUrl + groupClassRoom.getAll(query),
        {signal},
      );
      if (response.status === Status.Ok) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error in GetAllGroupClassRooms function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  GetServices: async (
    organizationUnitId?: number,
  ): Promise<GroupClassRoomServicesResponse> => {
    try {
      const unitId = toOptionalOrganizationUnitId(organizationUnitId);
      const response = await axiosInstance.get<GroupClassRoomServicesResponse>(
        baseUrl +
          groupClassRoom.getServices(
            unitId ? {organizationUnitId: unitId} : undefined,
          ),
      );
      if (response.status === Status.Ok) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in GetGroupClassRoomServices function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  GetParticipants: async (
    id: number,
    query: GroupClassRoomParticipantsQuery,
  ): Promise<GroupClassRoomParticipantsResponse> => {
    try {
      const response =
        await axiosInstance.get<GroupClassRoomParticipantsResponse>(
          baseUrl + groupClassRoom.getParticipants(id, query),
        );
      if (response.status === Status.Ok) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in GetGroupClassRoomParticipants function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  GetOrganizationUnit: async (): Promise<OrganizationUnitResponse> => {
    try {
      const response = await axiosInstance.get<OrganizationUnitResponse>(
        baseUrl + groupClassRoom.getOrganizationUnit(),
      );
      if (response.status === Status.Ok) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in GetOrganizationUnit function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },

  PreReserve: async (
    body: GroupClassRoomPreReserveQuery,
  ): Promise<AuthResponseSignUpDto> => {
    try {
      const response = await axiosInstance.post<
        GroupClassRoomPreReserveQuery,
        AxiosResponse<AuthResponseSignUpDto>
      >(baseUrl + groupClassRoom.preReserve(), body, {
        headers: {
          'CLIENT-REMOTE': 'true',
        },
      });
      if (response.status === Status.Ok || response.status === Status.Created) {
        return response.data;
      } else {
        throw new Error(`Request failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Error in PreReserveGroupClassRoom function:', error);
      if (axios.isAxiosError(error) && error.response) {
        handleMutationError(error);
        throw new Error(
          error.response?.data?.message || 'Unknown error occurred',
        );
      }
      throw error;
    }
  },
};

export default GroupClassRoomService;
